import { z } from 'zod';

import { IsoDateTimeSchema } from '../common/datetime';
import { ErrorCodeSchema } from '../common/errors';
import { TimezoneSchema } from '../common/fields';
import { AttendanceMarkId, SiteId, TerminalSessionId, VppChallengeId } from '../common/ids';
import {
  AutonomousTokenSchema,
  BootstrapTokenSchema,
  ChallengeShortIdSchema,
  ChallengeTokenSchema,
} from '../terminal/qr';

/**
 * Marcación con VPP · realm `staff` (feature 004, `contracts/staff-vpp.md`).
 *
 * Seis rutas, todas **autenticadas** (ninguna `config: { public: true }`):
 * `GET /attendance/state`, `POST /challenges`, `POST /challenges/{id}/wait`,
 * `POST /challenges/{id}/verify`, `POST /marks/autonomous` y el stream `GET /events`.
 *
 * Invariantes que estos esquemas hacen cumplir:
 * - La acción (`CHECK_IN`/`CHECK_OUT`) y la verificación las decide **el servidor**; lo que Staff
 *   envía como `proposed_action` sirve solo para detectar discrepancia (FR-007).
 * - Los instantes de captura son **relativos** al primer frame (`dt_ms`): el reloj del teléfono es
 *   telemetría, nunca verdad (R-6, constitución IV).
 * - La confirmación que ve el trabajador no lleva `challenge_id`, nonce, frames ni conteos
 *   (FR-032); el motivo de un rechazo nunca viaja en la respuesta (FR-023).
 */

// ---------------------------------------------------------------------------
// Tipos comunes
// ---------------------------------------------------------------------------

export const AttendanceActionSchema = z.enum(['CHECK_IN', 'CHECK_OUT']);
export type AttendanceAction = z.infer<typeof AttendanceActionSchema>;

export const AttendanceMarkStatusSchema = z.enum(['VALID', 'FLAGGED', 'SUPERSEDED', 'VOIDED']);
export type AttendanceMarkStatus = z.infer<typeof AttendanceMarkStatusSchema>;

export const AttendanceFlagSchema = z.enum([
  'LOCATION_MISMATCH',
  'LOCATION_UNAVAILABLE',
  'MISSING_CHECKOUT',
  'MARKER_OFFLINE',
]);
export type AttendanceFlag = z.infer<typeof AttendanceFlagSchema>;

export const LocationStatusSchema = z.enum(['MATCH', 'MISMATCH', 'LOW_ACCURACY', 'UNAVAILABLE']);
export type LocationStatus = z.infer<typeof LocationStatusSchema>;

export const VppModeSchema = z.enum(['NORMAL', 'AUTONOMOUS']);
export type VppMode = z.infer<typeof VppModeSchema>;

export const AttendanceStateSchema = z.enum(['IN', 'OUT', 'UNRESOLVED']);
export type AttendanceState = z.infer<typeof AttendanceStateSchema>;

export const ChallengeStatusSchema = z.enum([
  'QUEUED',
  'PENDING',
  'DISPLAYING',
  'VERIFIED',
  'USED',
  'EXPIRED',
  'FAILED',
]);
export type ChallengeStatus = z.infer<typeof ChallengeStatusSchema>;

/** FR-003 / R-24. `accuracy_m` en metros; `latency_ms` es telemetría de `LOCATION_RESULT`. */
export const LocationSampleSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  accuracy_m: z.number().nonnegative().max(100_000),
  latency_ms: z.number().int().nonnegative().max(60_000).optional(),
});
export type LocationSample = z.infer<typeof LocationSampleSchema>;

/** FR-035a. Staff la genera por intento y la repite en cada reintento de la misma evidencia. */
export const IdempotencyKeySchema = z.uuid();
export type IdempotencyKey = z.infer<typeof IdempotencyKeySchema>;

/** Sede tal como la muestra Staff en la confirmación (FR-032). */
export const MarkSiteRefSchema = z.object({
  id: SiteId,
  name: z.string(),
  timezone: TimezoneSchema,
});
export type MarkSiteRef = z.infer<typeof MarkSiteRefSchema>;

/**
 * Lo que Staff recibe de una marca creada. NO incluye challenge_id, nonce, frames ni conteos
 * (FR-032). `status` y `flags` viajan para el historial de la 006; la pantalla de marcación
 * MUST NOT renderizarlos: una marca FLAGGED se confirma igual que una VALID (US6-2, US8-2).
 */
export const AttendanceMarkConfirmationSchema = z.object({
  /** `mrk_<ULID>`. */
  id: AttendanceMarkId,
  type: AttendanceActionSchema,
  /** Hora oficial, UTC (FR-028, SC-011). */
  server_timestamp: IsoDateTimeSchema,
  /** `timezone` = la que Staff usa para formatear. */
  site: MarkSiteRefSchema,
  status: AttendanceMarkStatusSchema,
  flags: z.array(AttendanceFlagSchema),
  location_status: LocationStatusSchema,
  vpp_mode: VppModeSchema,
});
export type AttendanceMarkConfirmation = z.infer<typeof AttendanceMarkConfirmationSchema>;

/** Referencia al challenge que Staff necesita para pintar y capturar. Sin nonce ni tokens. */
export const ChallengeRefSchema = z.object({
  /** `chl_<ULID>`; viaja en la URL de `wait` y `verify`. */
  id: VppChallengeId,
  /** Los 8 caracteres del campo `c` de cada `FM1C`. */
  short_id: ChallengeShortIdSchema,
  /** La que el servidor calculó (FR-007). */
  action: AttendanceActionSchema,
  frames_count: z.number().int().min(4).max(10),
  frame_interval_ms: z.number().int().min(120).max(400),
  required_frames: z.number().int().min(3).max(10),
});
export type ChallengeRef = z.infer<typeof ChallengeRefSchema>;

/** `:id` de `POST /challenges/{id}/wait` y `POST /challenges/{id}/verify`. */
export const ChallengeIdParamsSchema = z.object({ id: VppChallengeId });
export type ChallengeIdParams = z.infer<typeof ChallengeIdParamsSchema>;

// ---------------------------------------------------------------------------
// 1. `GET /attendance/state` — acción propuesta y estado del trabajador
// ---------------------------------------------------------------------------

/** `200`. Lectura pura (FR-001, FR-007…FR-010): nunca responde `ATTENDANCE_STATE_UNRESOLVED`. */
export const AttendanceStateResponseSchema = z.object({
  state: AttendanceStateSchema,
  /** `null` solo si `state = 'UNRESOLVED'`. */
  proposed_action: AttendanceActionSchema.nullable(),
  last_mark: z
    .object({
      type: AttendanceActionSchema,
      server_timestamp: IsoDateTimeSchema,
      site: MarkSiteRefSchema,
    })
    .nullable(),
  /** FR-009: `IN` desde hace más de `max_open_shift_hours`. Staff muestra el aviso de US5-1. */
  open_shift_exceeded: z.boolean(),
  server_time: IsoDateTimeSchema,
});
export type AttendanceStateResponse = z.infer<typeof AttendanceStateResponseSchema>;

// ---------------------------------------------------------------------------
// 2. `POST /challenges` — crear un challenge
// ---------------------------------------------------------------------------

/**
 * Por qué no se pudo abrir la cámara en un intento **anterior** de esta misma marcación
 * (FR-036; eventos `CAMERA_DENIED`/`CAMERA_ERROR` de FR-038). Staff lo clasifica a partir del
 * `name` del error real de `getUserMedia` (research R-25: la Permissions API no se usa) y lo
 * adjunta al primer `POST /challenges` que sí llega a salir.
 *
 * Es un **catálogo cerrado de cuatro motivos**, nunca el `message` de la `DOMException`: ese texto
 * es libre, específico del equipo y del navegador ("Could not start video source", nombres de
 * dispositivo incluidos), así que no puede entrar en un log (SC-012) ni ser agrupado por nadie.
 *
 * | Valor | Qué pasó | Evento que emite la API |
 * |---|---|---|
 * | `DENIED` | el trabajador denegó el permiso (`NotAllowedError`/`SecurityError`) | `CAMERA_DENIED` |
 * | `UNAVAILABLE` | no hay cámara utilizable (`NotFoundError`/`OverconstrainedError`) | `CAMERA_ERROR` |
 * | `ERROR` | la cámara existe pero otra app la tiene tomada o falló al arrancar | `CAMERA_ERROR` |
 * | `UNSUPPORTED` | el navegador no expone `mediaDevices.getUserMedia` (contexto inseguro, WebView vieja) | `CAMERA_ERROR` |
 */
export const CameraFailureSchema = z.enum(['DENIED', 'UNAVAILABLE', 'ERROR', 'UNSUPPORTED']);
export type CameraFailure = z.infer<typeof CameraFailureSchema>;

/** `tsid` y `bt` salen de `decodeFerminQr(texto)` con `{ kind: 'bootstrap' }` (FR-004). */
export const CreateChallengeBodySchema = z.object({
  /** Del `FM1B` escaneado, con prefijo `tss_`. */
  tsid: TerminalSessionId,
  bt: BootstrapTokenSchema,
  /** La acción que Staff mostraba, SOLO para detectar discrepancia (FR-007). Nunca decide. */
  proposed_action: AttendanceActionSchema.nullable(),
  /** Si ya llegó (FR-003); si no, `null`. */
  location: LocationSampleSchema.nullable(),
  /** Telemetría de `VPP_INIT`. Nada de esto influye en ninguna decisión. */
  client: z
    .object({
      camera_open_ms: z.number().int().nonnegative().max(60_000).optional(),
      bootstrap_search_ms: z.number().int().nonnegative().max(60_000).optional(),
      /**
       * Motivo del fallo de cámara del intento anterior, si lo hubo (FR-038/FR-039). Se reporta
       * **una sola vez**: el intento que por fin abrió la cámara lo arrastra hasta aquí y lo
       * suelta. Solo el motivo del catálogo; nada del error del navegador.
       */
      camera_failure: CameraFailureSchema.optional(),
    })
    .optional(),
});
export type CreateChallengeBody = z.infer<typeof CreateChallengeBodySchema>;

/** `201`: el turno estaba libre. El challenge ya se envió al marcador (`challenge_start`). */
export const ChallengeCreatedResponseSchema = z.object({
  status: z.literal('PENDING'),
  challenge: ChallengeRefSchema,
  /** R-21: `issued_at`/`expires_at` se fijan AQUÍ, al promover a `PENDING`, no al encolar. */
  expires_at: IsoDateTimeSchema,
  server_time: IsoDateTimeSchema,
});
export type ChallengeCreatedResponse = z.infer<typeof ChallengeCreatedResponseSchema>;

/** `202`: terminal ocupado (FR-013). Staff mantiene la cámara abierta y empieza a latir. */
export const ChallengeQueuedResponseSchema = z.object({
  status: z.literal('QUEUED'),
  challenge: ChallengeRefSchema,
  position: z.number().int().min(1).max(10),
  /** `position × 2500` (vpp-protocol §9). */
  eta_ms: z.number().int().nonnegative(),
  /** Cada cuánto latir (FR-014a). */
  wait_interval_ms: z.number().int().min(500).max(5_000),
  /** `created_at + queue_max_wait_ms`. En `QUEUED` **no** existe todavía `expires_at` (R-21). */
  queue_expires_at: IsoDateTimeSchema,
  server_time: IsoDateTimeSchema,
});
export type ChallengeQueuedResponse = z.infer<typeof ChallengeQueuedResponseSchema>;

// ---------------------------------------------------------------------------
// 3. `POST /challenges/{id}/wait` — señal de espera en cola
// ---------------------------------------------------------------------------

/**
 * Cuerpo vacío **pero presente**: el realm exige CSRF en mutaciones y el canon de
 * `X-Fermin-Device` firma el hash del cuerpo.
 */
export const WaitChallengeBodySchema = z.object({});
export type WaitChallengeBody = z.infer<typeof WaitChallengeBodySchema>;

/**
 * `200`. Latido (FR-014) y a la vez degradación del SSE (FR-014a): lleva el mismo estado que
 * habría bajado por el stream. Un `status` terminal distinto de `USED` se responde como **error**
 * con su código (`410 CHALLENGE_EXPIRED` / `422 INSUFFICIENT_EVIDENCE`), no como `200`.
 */
export const WaitChallengeResponseSchema = z.object({
  status: ChallengeStatusSchema,
  /** Presentes solo mientras `status = 'QUEUED'`. */
  position: z.number().int().min(1).max(10).nullable(),
  eta_ms: z.number().int().nonnegative().nullable(),
  /** Presente desde `PENDING`. */
  expires_at: IsoDateTimeSchema.nullable(),
  /** Presente desde `DISPLAYING`: Staff puede anclar su captura aunque el SSE se haya caído. */
  display_started_at: IsoDateTimeSchema.nullable(),
  /** Presente solo en `USED`: permite recuperar la confirmación sin volver a verificar. */
  mark: AttendanceMarkConfirmationSchema.nullable(),
  server_time: IsoDateTimeSchema,
});
export type WaitChallengeResponse = z.infer<typeof WaitChallengeResponseSchema>;

// ---------------------------------------------------------------------------
// 4. `POST /challenges/{id}/verify` — verificar la evidencia
// ---------------------------------------------------------------------------

export const ObservedFrameSchema = z.object({
  i: z.number().int().min(1).max(10),
  /** 16 caracteres crockford32, tal cual se leyó del `FM1C`. */
  t: ChallengeTokenSchema,
  /** R-6: ms desde el PRIMER frame observado (`performance.now()`), no epoch. */
  dt_ms: z.number().int().nonnegative().max(30_000),
});
export type ObservedFrame = z.infer<typeof ObservedFrameSchema>;

export const VerifyChallengeBodySchema = z.object({
  /** Únicos por `i`, en orden de captura. El primero lleva `dt_ms = 0`. */
  frames: z.array(ObservedFrameSchema).min(1).max(20),
  /** FR-035a, la misma en los 2 reintentos de FR-035. */
  idempotency_key: IdempotencyKeySchema,
  location: LocationSampleSchema.nullable(),
});
export type VerifyChallengeBody = z.infer<typeof VerifyChallengeBodySchema>;

/** `200`. `idempotent = true` cuando el challenge ya estaba `USED` por el mismo principal. */
export const VerifyChallengeResponseSchema = z.object({
  mark: AttendanceMarkConfirmationSchema,
  idempotent: z.boolean(),
  server_time: IsoDateTimeSchema,
});
export type VerifyChallengeResponse = z.infer<typeof VerifyChallengeResponseSchema>;

// ---------------------------------------------------------------------------
// 5. `POST /marks/autonomous` — verificación en modo autónomo
// ---------------------------------------------------------------------------

export const AutonomousFrameSchema = z.object({
  slot: z.number().int().nonnegative(),
  /** 16 caracteres crockford32 del `FM1A`. */
  token: AutonomousTokenSchema,
  dt_ms: z.number().int().nonnegative().max(30_000),
});
export type AutonomousFrame = z.infer<typeof AutonomousFrameSchema>;

/** FR-043…FR-045. **No hay challenge**: el marcador está sin canal y emite frames `FM1A`. */
export const AutonomousMarkBodySchema = z.object({
  tsid: TerminalSessionId,
  frames: z.array(AutonomousFrameSchema).min(1).max(30),
  /** FR-044: ES el mecanismo de idempotencia aquí. */
  idempotency_key: IdempotencyKeySchema,
  proposed_action: AttendanceActionSchema.nullable(),
  location: LocationSampleSchema.nullable(),
});
export type AutonomousMarkBody = z.infer<typeof AutonomousMarkBodySchema>;

/** `201`. La marca nace `FLAGGED` con `MARKER_OFFLINE` **siempre** (FR-044). */
export const AutonomousMarkResponseSchema = z.object({
  mark: AttendanceMarkConfirmationSchema,
  idempotent: z.boolean(),
  server_time: IsoDateTimeSchema,
});
export type AutonomousMarkResponse = z.infer<typeof AutonomousMarkResponseSchema>;

// ---------------------------------------------------------------------------
// 6. `GET /events` — flujo de eventos del realm staff (SSE)
// ---------------------------------------------------------------------------

/**
 * Se consume con `fetch` + `ReadableStream`, **nunca** con `EventSource`: el realm staff exige la
 * cabecera firmada `X-Fermin-Device` y `EventSource` no admite cabeceras. Ningún mensaje lleva
 * tokens, frames, nonces ni documentos (FR-018, SC-012).
 */
export const StaffStreamOpenSchema = z.object({
  type: z.literal('stream_open'),
  server_time: IsoDateTimeSchema,
  /** Cada cuánto debe latir Staff mientras espere en cola (FR-014a). */
  wait_interval_ms: z.number().int().min(500).max(5_000),
});
export type StaffStreamOpen = z.infer<typeof StaffStreamOpenSchema>;

/** Cada vez que la posición cambia; nunca en un temporizador fijo. */
export const StaffQueuePositionSchema = z.object({
  type: z.literal('queue_position'),
  challenge_id: VppChallengeId,
  position: z.number().int().min(1).max(10),
  eta_ms: z.number().int().nonnegative(),
  server_time: IsoDateTimeSchema,
});
export type StaffQueuePosition = z.infer<typeof StaffQueuePositionSchema>;

/** El marcador acusó el inicio de reproducción (FR-019): Staff ya puede anclar su captura. */
export const StaffChallengeDisplayingSchema = z.object({
  type: z.literal('challenge_displaying'),
  challenge_id: VppChallengeId,
  /** Reloj del SERVIDOR. */
  display_started_at: IsoDateTimeSchema,
  expires_at: IsoDateTimeSchema,
  frames_count: z.number().int().min(4).max(10),
  frame_interval_ms: z.number().int().min(120).max(400),
});
export type StaffChallengeDisplaying = z.infer<typeof StaffChallengeDisplayingSchema>;

/**
 * Resultado final. `error_code` es el MISMO código opaco que devolvería la ruta HTTP (FR-023).
 * Es redundancia: la confirmación autoritativa siempre es el `200` de `verify`.
 */
export const StaffChallengeResultSchema = z.object({
  type: z.literal('challenge_result'),
  challenge_id: VppChallengeId,
  status: z.enum(['USED', 'FAILED', 'EXPIRED']),
  mark: AttendanceMarkConfirmationSchema.nullable(),
  error_code: ErrorCodeSchema.nullable(),
  server_time: IsoDateTimeSchema,
});
export type StaffChallengeResult = z.infer<typeof StaffChallengeResultSchema>;

/** Unión discriminada de todo lo que baja por el stream de staff. */
export const StaffStreamMessageSchema = z.discriminatedUnion('type', [
  StaffStreamOpenSchema,
  StaffQueuePositionSchema,
  StaffChallengeDisplayingSchema,
  StaffChallengeResultSchema,
]);
export type StaffStreamMessage = z.infer<typeof StaffStreamMessageSchema>;
